/**
 * useGoogleReviews — shared hook backed by the Featurable JSON API.
 *
 * Both the hero trust pill and the dedicated GoogleReviewsEmbed section
 * need the same data (rating + total count), so we lift the fetch up
 * here and cache the result at module scope. The first component to
 * mount kicks off the request; everyone else gets the same Promise
 * back and only pays for one round-trip per page load.
 *
 * Returns `{ data, error, loading }`. `data` matches the Featurable
 * payload shape: `{ averageRating, totalReviewCount, profileUrl,
 * reviews: [...] }`.
 */
import { useEffect, useState } from 'react';

const FEATURABLE_WIDGET_ID = '6ed925bc-7bbd-42c7-b88b-3237ca132774';
const FEATURABLE_API = `https://api.featurable.com/v1/widgets/${FEATURABLE_WIDGET_ID}`;

// Module-level cache + in-flight dedup so multiple mounted components
// share a single network call per page session.
let cachedData = null;
let cachedError = null;
let inflight = null;

function fetchReviews() {
  if (cachedData) return Promise.resolve(cachedData);
  if (inflight) return inflight;
  inflight = fetch(FEATURABLE_API, { headers: { Accept: 'application/json' } })
    .then((r) => {
      if (!r.ok) throw new Error(`Featurable ${r.status}`);
      return r.json();
    })
    .then((json) => {
      cachedData = json;
      inflight = null;
      return json;
    })
    .catch((e) => {
      cachedError = e;
      inflight = null;
      throw e;
    });
  return inflight;
}

export default function useGoogleReviews() {
  const [data, setData] = useState(cachedData);
  const [error, setError] = useState(cachedError);
  const [loading, setLoading] = useState(!cachedData && !cachedError);

  useEffect(() => {
    if (cachedData || cachedError) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchReviews()
      .then((json) => { if (!cancelled) { setData(json); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  return { data, error, loading };
}
